import { useState, useEffect } from 'react';
import { Link2, Mail, Copy, CheckCircle2, QrCode, ExternalLink, X, Globe, Image as ImageIcon, User, Sparkles, Search, Heart, Download, Check, Frame, PauseCircle, PlayCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import QRCodeModal from '../../QRCodeModal';

const ShareLinksPanel = ({
  gallery,
  galleryId,
  axiosConfig,
  backendUrl,
  isCrew,
  onRefreshGallery
}) => {
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [emailList, setEmailList] = useState([]);
  const [currentEmail, setCurrentEmail] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sendingProgress, setSendingProgress] = useState({ current: 0, total: 0 });
  const [showQRModal, setShowQRModal] = useState(false);
  const [qrUrl, setQrUrl] = useState('');
  const [qrTitle, setQrTitle] = useState('');
  const [copiedLink, setCopiedLink] = useState(null);
  const [selectedLinks, setSelectedLinks] = useState({});
  const [savingSharing, setSavingSharing] = useState(false);

  // Absent means on: every gallery that predates this switch keeps working.
  const sharingEnabled = gallery?.sharing_enabled !== false;

  const setSharing = async (enabled) => {
    try {
      setSavingSharing(true);
      await axios.put(`${backendUrl}/api/galleries/${galleryId}/sharing`, { enabled }, axiosConfig);
      toast.success(enabled ? 'Sharing is back on — every link works again' : 'Sharing paused — every link is now closed');
      if (onRefreshGallery) await onRefreshGallery();
    } catch (error) {
      console.error('Error updating sharing:', error);
      toast.error('Could not change sharing. Try again.');
    } finally {
      setSavingSharing(false);
    }
  };

  const shareLinks = [
    {
      id: 'full',
      title: 'Full Gallery',
      description: 'Complete access with all features enabled',
      token: gallery?.share_token,
      path: '/share/',
      badge: 'All Features',
      badgeColor: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 border-blue-200 dark:border-blue-800',
      gradient: 'from-blue-500 to-blue-600',
      iconBg: 'bg-blue-500',
      icon: Globe,
      features: [
        { icon: Sparkles, label: 'AI Tag Search' },
        { icon: Search, label: 'Face Search' },
        { icon: Heart, label: 'Favorites' },
        { icon: Download, label: 'Bulk Download' }
      ]
    },
    {
      id: 'simple',
      title: 'Simple Gallery',
      description: 'Basic view optimized for quick browsing',
      token: gallery?.simple_share_token,
      path: '/simple/',
      badge: 'Simple',
      badgeColor: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300 border-green-200 dark:border-green-800',
      gradient: 'from-green-500 to-green-600',
      iconBg: 'bg-green-500',
      icon: ImageIcon,
      features: [
        { icon: Search, label: 'Face Search' },
        { icon: Download, label: 'Individual Downloads' }
      ]
    },
    {
      id: 'findme',
      title: 'Find My Photos',
      description: 'Direct face search landing page',
      token: gallery?.find_me_token,
      path: '/findme/',
      badge: 'Face Only',
      badgeColor: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300 border-purple-200 dark:border-purple-800',
      gradient: 'from-purple-500 to-purple-600',
      iconBg: 'bg-purple-500',
      icon: User,
      features: [
        { icon: Search, label: 'Direct Face Search' },
        { icon: Download, label: 'Quick Access' }
      ]
    },
    // Branded gallery link - only shown when configured
    ...(gallery?.branded_share_token && gallery?.frames?.some(f => f.is_active) ? [{
      id: 'branded',
      title: 'Branded Gallery',
      description: 'Photos with branded frame overlay',
      token: gallery.branded_share_token,
      path: '/branded/',
      badge: 'Branded',
      badgeColor: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 border-amber-200 dark:border-amber-800',
      gradient: 'from-amber-500 to-amber-600',
      iconBg: 'bg-amber-500',
      icon: Frame,
      features: [
        { icon: Frame, label: 'Branded Frame Overlay' },
        { icon: Download, label: 'Framed Downloads' }
      ]
    }] : [])
  ];

  // Backend uses "find_me" but the frontend uses "findme" for the URL path.
  const toBackendKey = (id) => (id === 'findme' ? 'find_me' : id);

  const availableLinkOptions = shareLinks
    .filter((l) => l.token)
    .map((l) => ({
      key: toBackendKey(l.id),
      title: l.title,
      badge: l.badge,
      badgeColor: l.badgeColor,
      iconBg: l.iconBg,
      icon: l.icon,
    }));

  // Reset selection to "all available checked" each time the dialog opens.
  // Keyed on a stable string of the available link types so changes to
  // gallery tokens also reset cleanly without re-running on every render.
  const availableKeysSignature = availableLinkOptions.map((o) => o.key).join(',');
  useEffect(() => {
    if (!showEmailDialog) return;
    const initial = {};
    availableKeysSignature.split(',').filter(Boolean).forEach((key) => { initial[key] = true; });
    setSelectedLinks(initial);
  }, [showEmailDialog, availableKeysSignature]);

  const selectedCount = Object.values(selectedLinks).filter(Boolean).length;

  const copyToClipboard = async (url, linkId) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedLink(linkId);
      toast.success('Link copied to clipboard!');
      setTimeout(() => setCopiedLink(null), 2000);
    } catch (err) {
      toast.error('Failed to copy link');
    }
  };

  const openQRModal = (url, title) => {
    setQrUrl(url);
    setQrTitle(title);
    setShowQRModal(true);
  };

  const handleAddEmail = () => {
    const email = currentEmail.trim().toLowerCase();
    if (!email) return;

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    if (emailList.includes(email)) {
      toast.error('This email is already in the list');
      return;
    }

    setEmailList([...emailList, email]);
    setCurrentEmail('');
  };

  const handleRemoveEmail = (emailToRemove) => {
    setEmailList(emailList.filter(e => e !== emailToRemove));
  };

  const handleSendInvitations = async () => {
    if (emailList.length === 0) {
      toast.error('Please add at least one email address');
      return;
    }

    const selected = Object.entries(selectedLinks)
      .filter(([, checked]) => checked)
      .map(([key]) => key);

    if (selected.length === 0) {
      toast.error('Please select at least one gallery link to include');
      return;
    }

    setSending(true);
    setSendingProgress({ current: 0, total: emailList.length });

    try {
      const response = await axios.post(
        `${backendUrl}/api/admin/gallery/${galleryId}/send-invitation`,
        {
          emails: emailList,
          message: emailMessage.trim(),
          inviter_name: 'Admin',
          selected_links: selected,
        },
        axiosConfig
      );

      for (let i = 0; i <= emailList.length; i++) {
        setSendingProgress({ current: i, total: emailList.length });
        await new Promise(resolve => setTimeout(resolve, 200));
      }

      toast.success(`Successfully sent ${response.data.successful} invitation(s)`);

      if (response.data.failed > 0) {
        toast.error(`Failed to send ${response.data.failed} invitation(s)`);
      }

      setShowEmailDialog(false);
      setEmailList([]);
      setCurrentEmail('');
      setEmailMessage('');
    } catch (error) {
      console.error('Error sending invitations:', error);
      toast.error('Failed to send invitations');
    } finally {
      setSending(false);
      setSendingProgress({ current: 0, total: 0 });
    }
  };

  return (
    <div className="h-full overflow-auto p-3 md:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4 md:mb-6">
        <div>
          <h2 className="text-xl md:text-2xl font-bold text-slate-800 dark:text-white" style={{ fontFamily: 'Archivo, sans-serif' }}>
            Share Links
          </h2>
          <p className="text-xs md:text-sm text-slate-500 dark:text-slate-400 mt-1">
            Share gallery access with clients and guests
          </p>
        </div>
        <Button
          onClick={() => setShowEmailDialog(true)}
          className="bg-[#BF212F] hover:from-[#8B1923] hover:to-[#BF212F] h-10 px-4 w-full sm:w-auto"
        >
          <Mail className="w-4 h-4 mr-2" />
          Email All Links
        </Button>
      </div>

      {/* Master switch — governs every card below */}
      <div
        className={`flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 mb-4 md:mb-6 rounded-xl border ${
          sharingEnabled
            ? 'bg-slate-50 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700'
            : 'bg-amber-50 dark:bg-amber-900/20 border-amber-300 dark:border-amber-800'
        }`}
      >
        <div className="flex items-start gap-3">
          {sharingEnabled ? (
            <PlayCircle className="w-5 h-5 mt-0.5 shrink-0 text-slate-400" />
          ) : (
            <PauseCircle className="w-5 h-5 mt-0.5 shrink-0 text-amber-600 dark:text-amber-400" />
          )}
          <div>
            <div className="text-sm font-semibold text-slate-800 dark:text-white">
              {sharingEnabled ? 'Sharing is on' : 'Sharing is paused'}
            </div>
            <p className="text-xs text-slate-600 dark:text-slate-300 mt-0.5 max-w-xl">
              {sharingEnabled
                ? 'Anyone with a link below can open this gallery.'
                : 'Every link below is closed — photos, downloads and zips included. The links themselves are untouched, so they all work again the moment you switch this back on.'}
            </p>
            {!sharingEnabled && gallery?.sharing_disabled_at && (
              <p className="text-xs text-amber-700 dark:text-amber-400 mt-1">
                Paused {new Date(gallery.sharing_disabled_at).toLocaleString('en-AU', {
                  day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit',
                })}
                {gallery.sharing_disabled_by ? ` by ${gallery.sharing_disabled_by}` : ''}
              </p>
            )}
          </div>
        </div>
        {!isCrew && (
          <button
            type="button"
            disabled={savingSharing}
            onClick={() => setSharing(!sharingEnabled)}
            aria-label={sharingEnabled ? 'Pause sharing' : 'Resume sharing'}
            className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors self-start sm:self-auto disabled:opacity-50 ${
              sharingEnabled ? 'bg-green-500' : 'bg-slate-300 dark:bg-slate-600'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                sharingEnabled ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        )}
      </div>

      {/* Share Link Cards */}
      <div
        className={`grid gap-4 md:gap-6 grid-cols-1 md:grid-cols-3 ${
          sharingEnabled ? '' : 'opacity-50'
        }`}
      >
        {shareLinks.map((link) => {
          if (!link.token) return null;

          const fullUrl = `${window.location.origin}${link.path}${link.token}`;
          const Icon = link.icon;

          return (
            <Card key={link.id} className="overflow-hidden border-0 shadow-md hover:shadow-lg transition-shadow duration-200">
              {/* Header with Icon */}
              <div className={`p-5 bg-gradient-to-br ${link.gradient} text-white relative overflow-hidden`}>
                {/* Decorative circles */}
                <div className="absolute -right-4 -top-4 w-24 h-24 bg-white/10 rounded-full" />
                <div className="absolute -right-2 top-8 w-16 h-16 bg-white/5 rounded-full" />

                <div className="relative">
                  <div className="flex items-start justify-between mb-3">
                    <div className="p-2.5 bg-white/20 backdrop-blur-sm rounded-xl">
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <Badge variant="outline" className="bg-white/20 text-white border-white/30 text-xs font-medium">
                      {link.badge}
                    </Badge>
                  </div>
                  <h3 className="text-lg font-bold mb-1">{link.title}</h3>
                  <p className="text-sm text-white/80">{link.description}</p>
                </div>
              </div>

              {/* Features List */}
              <div className="p-4 border-b border-slate-100 dark:border-slate-700">
                <div className="grid grid-cols-2 gap-2">
                  {link.features.map((feature, idx) => {
                    const FeatureIcon = feature.icon;
                    return (
                      <div key={idx} className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
                        <Check className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
                        <span className="truncate">{feature.label}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* URL and Actions */}
              <div className="p-4 space-y-3">
                {/* URL Display */}
                <div className="flex items-center gap-2 p-2.5 bg-slate-50 dark:bg-slate-700/50 rounded-lg border border-slate-100 dark:border-slate-600">
                  <Link2 className="w-4 h-4 text-slate-400 flex-shrink-0" />
                  <span className="text-xs text-slate-500 dark:text-slate-400 truncate flex-1 font-mono">
                    {fullUrl}
                  </span>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-2">
                  <Button
                    variant={copiedLink === link.id ? "default" : "outline"}
                    size="sm"
                    className={`flex-1 transition-all duration-200 ${
                      copiedLink === link.id
                        ? 'bg-green-500 hover:bg-green-600 text-white border-green-500'
                        : ''
                    }`}
                    onClick={() => copyToClipboard(fullUrl, link.id)}
                  >
                    {copiedLink === link.id ? (
                      <>
                        <CheckCircle2 className="w-4 h-4 mr-1.5" />
                        Copied!
                      </>
                    ) : (
                      <>
                        <Copy className="w-4 h-4 mr-1.5" />
                        Copy Link
                      </>
                    )}
                  </Button>

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openQRModal(fullUrl, link.title)}
                    className="px-3"
                  >
                    <QrCode className="w-4 h-4" />
                  </Button>

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => window.open(fullUrl, '_blank')}
                    className="px-3"
                  >
                    <ExternalLink className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Tips */}
      <Card className="mt-6 overflow-hidden border-0 shadow-sm">
        <div className="p-4 bg-gradient-to-r from-slate-50 to-slate-100 dark:from-slate-800 dark:to-slate-700/50 border-b border-slate-200 dark:border-slate-600">
          <h4 className="font-semibold text-slate-800 dark:text-white flex items-center gap-2">
            <span className="p-1.5 rounded-lg bg-amber-100 dark:bg-amber-900/30">
              <Sparkles className="w-4 h-4 text-amber-600 dark:text-amber-400" />
            </span>
            Tips for Sharing
          </h4>
        </div>
        <div className="p-4">
          <div className="grid md:grid-cols-2 gap-3">
            <div className="flex gap-3 p-3 bg-blue-50/50 dark:bg-blue-900/10 rounded-lg">
              <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg h-fit">
                <Globe className="w-4 h-4 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-slate-700 dark:text-slate-200">Full Gallery</p>
                <p className="text-xs text-slate-500 dark:text-slate-400">Best for clients who need all features and bulk downloads</p>
              </div>
            </div>
            <div className="flex gap-3 p-3 bg-green-50/50 dark:bg-green-900/10 rounded-lg">
              <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg h-fit">
                <ImageIcon className="w-4 h-4 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-slate-700 dark:text-slate-200">Simple Gallery</p>
                <p className="text-xs text-slate-500 dark:text-slate-400">Good for quick browsing without AI features</p>
              </div>
            </div>
            <div className="flex gap-3 p-3 bg-purple-50/50 dark:bg-purple-900/10 rounded-lg">
              <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg h-fit">
                <User className="w-4 h-4 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-slate-700 dark:text-slate-200">Find My Photos</p>
                <p className="text-xs text-slate-500 dark:text-slate-400">Perfect for event attendees to find themselves</p>
              </div>
            </div>
            <div className="flex gap-3 p-3 bg-orange-50/50 dark:bg-orange-900/10 rounded-lg">
              <div className="p-2 bg-orange-100 dark:bg-orange-900/30 rounded-lg h-fit">
                <Mail className="w-4 h-4 text-orange-600 dark:text-orange-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-slate-700 dark:text-slate-200">Email All Links</p>
                <p className="text-xs text-slate-500 dark:text-slate-400">Send all options at once with a personal message</p>
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Email Dialog */}
      <Dialog open={showEmailDialog} onOpenChange={setShowEmailDialog}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold" style={{ fontFamily: 'Archivo, sans-serif' }}>
              Email Gallery Links
            </DialogTitle>
            <DialogDescription>
              Choose which share links to include, then send to one or more recipients for "{gallery?.name}".
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Link Selection */}
            <div className="space-y-2">
              <Label>
                Include in email <span className="text-red-500">*</span>
              </Label>
              <div className="border rounded-lg p-3 bg-slate-50 dark:bg-slate-700/40 space-y-2">
                {availableLinkOptions.map((opt) => {
                  const Icon = opt.icon;
                  return (
                    <label
                      key={opt.key}
                      htmlFor={`link-${opt.key}`}
                      className="flex items-center gap-3 px-2 py-2 rounded-md hover:bg-white dark:hover:bg-slate-700 cursor-pointer transition-colors"
                    >
                      <Checkbox
                        id={`link-${opt.key}`}
                        checked={!!selectedLinks[opt.key]}
                        onCheckedChange={(checked) =>
                          setSelectedLinks((prev) => ({ ...prev, [opt.key]: checked === true }))
                        }
                        disabled={sending}
                      />
                      <span className={`p-1.5 rounded-md ${opt.iconBg} text-white flex-shrink-0`}>
                        <Icon className="w-3.5 h-3.5" />
                      </span>
                      <span className="text-sm font-medium text-slate-700 dark:text-slate-200 flex-1">
                        {opt.title}
                      </span>
                      <Badge variant="outline" className={`text-[10px] font-medium ${opt.badgeColor}`}>
                        {opt.badge}
                      </Badge>
                    </label>
                  );
                })}
              </div>
              {selectedCount === 0 && (
                <p className="text-xs text-red-600 dark:text-red-400">
                  Select at least one gallery link to include.
                </p>
              )}
            </div>

            {/* Add Email Input */}
            <div className="space-y-2">
              <Label htmlFor="current-email">
                Add Email Address <span className="text-red-500">*</span>
              </Label>
              <div className="flex gap-2">
                <Input
                  id="current-email"
                  type="email"
                  placeholder="recipient@email.com"
                  value={currentEmail}
                  onChange={(e) => setCurrentEmail(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddEmail();
                    }
                  }}
                  disabled={sending}
                  className="flex-1"
                />
                <Button
                  onClick={handleAddEmail}
                  disabled={sending || !currentEmail.trim()}
                  variant="outline"
                  type="button"
                >
                  Add
                </Button>
              </div>
            </div>

            {/* Email List */}
            {emailList.length > 0 && (
              <div className="space-y-2">
                <Label>Recipients ({emailList.length})</Label>
                <div className="border rounded-lg p-3 max-h-[200px] overflow-y-auto bg-slate-50 dark:bg-slate-700">
                  <div className="space-y-2">
                    {emailList.map((email, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between bg-white dark:bg-slate-800 px-3 py-2 rounded border"
                      >
                        <span className="text-sm">{email}</span>
                        <Button
                          onClick={() => handleRemoveEmail(email)}
                          disabled={sending}
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0 hover:bg-red-50 hover:text-red-600"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Personal Message */}
            <div className="space-y-2">
              <Label htmlFor="email-message">Personal Message (Optional)</Label>
              <textarea
                id="email-message"
                placeholder="Add a personal message to include in the email..."
                value={emailMessage}
                onChange={(e) => setEmailMessage(e.target.value)}
                disabled={sending}
                className="w-full min-h-[80px] px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#BF212F] dark:bg-slate-700"
              />
            </div>

            {/* Sending Progress */}
            {sending && (
              <div className="space-y-2 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-blue-800 font-medium">Sending invitations...</span>
                  <span className="text-blue-600">
                    {sendingProgress.current} / {sendingProgress.total}
                  </span>
                </div>
                <div className="w-full bg-blue-200 rounded-full h-2">
                  <div
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${(sendingProgress.current / sendingProgress.total) * 100}%` }}
                  />
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowEmailDialog(false);
                setEmailList([]);
                setCurrentEmail('');
                setEmailMessage('');
              }}
              disabled={sending}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSendInvitations}
              disabled={sending || emailList.length === 0 || selectedCount === 0}
              className="bg-[#BF212F] hover:bg-[#8B1923]"
            >
              {sending ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  Sending...
                </>
              ) : (
                <>
                  <Mail className="w-4 h-4 mr-2" />
                  Send Invitations
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* QR Code Modal */}
      <QRCodeModal
        open={showQRModal}
        onClose={() => setShowQRModal(false)}
        url={qrUrl}
        title={qrTitle}
      />
    </div>
  );
};

export default ShareLinksPanel;
